import { FeatureFlag, OrganizationEntity, OrganizationSettingsEntity } from '@/generated/nabisNYWebAPI.schemas';
import { useOrganizationsControllerFindOneByUrlSlug } from '@/generated/organizations/organizations';
import { getOrganizationLinks, myOrganizations } from '@/modules/link';
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { KeyedMutator } from 'swr';
import { useOrganizationSettingsControllerFindOneByOrganizationId } from '@/generated/organization-settings/organization-settings';
import { useFeatureFlagControllerFindOne } from '@/generated/feature-flag/feature-flag';
import { useOrganizationMarketplaceSettingsControllerFindOneByOrganizationId } from '@/generated/organization-marketplace-settings/organization-marketplace-settings';

export const OrganizationContext = React.createContext<{
  organizationId: string;
  organization?: OrganizationEntity;
  organizationIsLoading: boolean;
  organizationLinks: ReturnType<typeof getOrganizationLinks>;
  refetch: KeyedMutator<AxiosResponse<OrganizationEntity>>;
  taxInclusivePricing: boolean;
  organizationSettings?: OrganizationSettingsEntity;
  organizationSettingsIsLoading: boolean;
  marketplaceEnabled: boolean;
  marketplaceActive: boolean;
}>({
  organizationId: '',
  organization: undefined,
  organizationIsLoading: false,
  organizationLinks: getOrganizationLinks(''),
  refetch: () => Promise.resolve(undefined),
  taxInclusivePricing: false,
  organizationSettings: undefined,
  organizationSettingsIsLoading: false,
  marketplaceEnabled: false,
  marketplaceActive: false,
});

export function useOrganizationContext() {
  const context = React.useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within UserProvider');
  }
  return context;
}

export const OrganizationProvider = (props: React.PropsWithChildren<unknown>) => {
  const router = useRouter();
  const { org: organizationUrlSlug } = router.query as { org: string };

  const isOrgRoute = router.pathname.indexOf('/organizations') !== -1;

  const {
    data: response,
    mutate: refetch,
    isLoading: organizationIsLoading,
  } = useOrganizationsControllerFindOneByUrlSlug(organizationUrlSlug, {
    swr: {
      // skip to allow provider on non-orgRoutes.
      enabled: Boolean(isOrgRoute && organizationUrlSlug),
      // don't refetch on focus
      revalidateOnFocus: false,
      onError: () => {
        void router.push(myOrganizations);
      },
    },
  });

  const organization = response?.data;

  const { data: v2OrderFormFlag } = useFeatureFlagControllerFindOne(FeatureFlag['v-2-order-form'], {
    organizationAlias: organization?.urlSlug,
  });

  const { data: marketPlaceSettings } = useFeatureFlagControllerFindOne(FeatureFlag['brand-marketplace-settings'], {
    organizationAlias: organization?.urlSlug,
  });

  const { data: marketplaceSettingsData } = useOrganizationMarketplaceSettingsControllerFindOneByOrganizationId(
    organization?.id || '',
  );

  const memoizedOrganizationLinks = useMemo(
    () => getOrganizationLinks(organization?.urlSlug || '', v2OrderFormFlag?.data?.featureFlagEnabled),
    [v2OrderFormFlag, organization?.urlSlug],
  );

  const { data: organizationSettingsData, isLoading: organizationSettingsIsLoading } =
    useOrganizationSettingsControllerFindOneByOrganizationId(organization?.id || '');

  return (
    <OrganizationContext.Provider
      value={{
        organizationId: organization?.id || '',
        organization,
        organizationIsLoading,
        organizationLinks: memoizedOrganizationLinks,
        refetch,
        taxInclusivePricing: organization?.taxInclusivePricing || false,
        organizationSettings: organizationSettingsData?.data,
        organizationSettingsIsLoading,
        marketplaceEnabled: marketPlaceSettings?.data?.featureFlagEnabled || false,
        marketplaceActive: marketplaceSettingsData?.data?.isEnabled || false,
      }}
    >
      {props.children}
    </OrganizationContext.Provider>
  );
};
